import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PlugZap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Loading } from "@/components/Widgets";
import { API, useApi, errMsg } from "@/lib/api";

const Row = ({ ok, label, testid }) => (
  <li data-testid={testid} className="flex items-center gap-2 text-sm py-2 border-b border-[#E8E8E4]">
    {ok ? <CheckCircle2 className="w-4 h-4 text-[#2A4B3C]" /> : <XCircle className="w-4 h-4 text-[#C05C3D]" />}{label}
  </li>
);

const STEPS = [
  "Converti Instagram in account Professionale (Business o Creator) e collegalo alla Pagina Facebook di EasyHost.",
  "Crea un'app su developers.facebook.com (tipo Business) e aggiungi i prodotti 'Facebook Login for Business' e 'Instagram Graph API'.",
  "Da Graph API Explorer genera un User Token con i permessi: pages_show_list, pages_read_engagement, pages_read_user_content, read_insights, instagram_basic, instagram_manage_insights, instagram_manage_comments, business_management.",
  "Scambia il token con uno a lunga durata e chiama /me/accounts: copia l'access_token e l'id della Pagina EasyHost.",
  "Inserisci META_PAGE_ACCESS_TOKEN e META_PAGE_ID nella scheda sicura delle chiavi (chiedimelo in chat e ti apro il form).",
];

export default function Settings() {
  const { data, loading, reload } = useApi("/status");
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  if (loading && !data) return <Loading />;
  const toggle = async (v) => {
    await axios.put(`${API}/settings`, { demo_mode: v });
    toast.success(v ? "Modalità demo attivata" : "Modalità live attivata");
    reload();
    setTimeout(() => window.location.reload(), 400);
  };
  const runTest = async () => {
    setTesting(true);
    try {
      const r = await axios.get(`${API}/test-connection`);
      setTest(r.data);
    } catch (e) {
      setTest({ ok: false, message: errMsg(e) });
    } finally {
      setTesting(false);
    }
  };
  return (
    <>
      <PageHeader eyebrow={`Meta Graph API ${data.api_version}`} title="Impostazioni" desc="Collega la Pagina Facebook e l'account Instagram Business di EasyHost. Il token resta sul server e non viene mai mostrato nel browser." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Stato connessione" testid="connection-status">
          <ul>
            <Row testid="status-token" ok={data.has_token} label="Page Access Token configurato" />
            <Row testid="status-page" ok={data.has_page_id} label="Page ID configurato" />
            <Row testid="status-live" ok={data.mode === "live"} label="Dati live Meta attivi" />
          </ul>
          <Button data-testid="test-connection-btn" onClick={runTest} disabled={testing} variant="outline" className="rounded-full mt-4"><PlugZap className="w-4 h-4 mr-2" />Testa connessione</Button>
          {test && <p data-testid="test-result" className={`text-sm mt-3 ${test.ok ? "text-[#2A4B3C]" : "text-[#C05C3D]"}`}>{test.ok ? `Connesso a ${test.page}${test.instagram ? ` · @${test.instagram}` : ""}` : test.message}</p>}
          {test?.checks && (
            <ul className="mt-3" data-testid="permission-checks">
              {test.checks.map((c) => <Row key={c.label} testid={`perm-${c.label.split(" ")[0]}`} ok={c.ok} label={c.label} />)}
            </ul>
          )}
          {test?.checks?.some((c) => !c.ok) && <p className="text-xs text-[#C05C3D] mt-2">Rigenera il token aggiungendo i permessi mancanti (vedi guida sotto), poi aggiornalo nelle chiavi.</p>}
        </Panel>
        <Panel title="Modalità demo" testid="demo-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#5C5C58] pr-4">Usa dati di esempio realistici anche con le credenziali configurate.</p>
            <Switch data-testid="demo-toggle" checked={data.mode === "demo"} disabled={!(data.has_token && data.has_page_id)} onCheckedChange={toggle} />
          </div>
          {!(data.has_token && data.has_page_id) && <p className="text-xs text-[#C05C3D] mt-3">Senza credenziali Meta l'app usa automaticamente i dati demo.</p>}
        </Panel>
        <Panel title="Aggiornamento dati" testid="cache-panel">
          <p className="text-sm text-[#5C5C58]">I dati live vengono memorizzati per 15 minuti per rispettare i limiti di Meta. Usa "Aggiorna" in Panoramica per forzare il refresh.</p>
        </Panel>
      </div>
      <Panel title="Come ottenere le credenziali Meta" className="mt-4" testid="setup-guide">
        <ol className="space-y-3">{STEPS.map((s, i) => <li key={i} className="flex gap-4 text-sm"><span className="font-mono-num text-[#C05C3D]">0{i + 1}</span>{s}</li>)}</ol>
      </Panel>
    </>
  );
}
